import apiClient from './client';

// ===== Notion Sync API =====

// 단일 경기 노션 동기화
export const syncMatchToNotion = async (
  matchId: number,
  aiProvider?: string,
  forceUpdate: boolean = false
): Promise<{
  status: string;
  data: {
    match_id: number;
    status: string;
    notion_page_exists: boolean;
    match_synced: boolean;
    prediction_synced: boolean;
    message: string;
  };
}> => {
  const params = new URLSearchParams();
  if (aiProvider) params.append('ai_provider', aiProvider);
  if (forceUpdate) params.append('force_update', 'true');

  const response = await apiClient.post(`/api/notion/sync/match/${matchId}?${params.toString()}`);
  return response.data;
};

// 날짜별 경기 노션 동기화
export const syncMatchesByDateToNotion = async (
  sport: string,
  date: string,
  aiProvider?: string,
  forceUpdate: boolean = false,
  adminId?: number
): Promise<{
  status: string;
  data: {
    sport: string;
    date: string;
    total_matches: number;
    synced_count: number;
    skipped_count: number;
    failed_count: number;
    details: Array<{
      match_id: number;
      status: string;
      notion_page_exists: boolean;
      match_synced: boolean;
      prediction_synced: boolean;
      message: string;
    }>;
  };
}> => {
  const params = new URLSearchParams({
    sport: sport,
    date: date
  });
  if (aiProvider) params.append('ai_provider', aiProvider);
  if (forceUpdate) params.append('force_update', 'true');
  if (adminId) params.append('admin_id', adminId.toString());

  const response = await apiClient.post(`/api/notion/sync/by-date?${params.toString()}`);
  return response.data;
};

// 미동기화 예측 일괄 동기화
export const syncUnsyncedToNotion = async (
  sport?: string,
  daysBack: number = 7,
  aiProvider?: string,
  adminId?: number
): Promise<{
  status: string;
  data: {
    total_predictions_checked: number;
    synced_count: number;
    already_exists_count: number;
    failed_count: number;
    details: Array<{
      match_id: number;
      teams: string;
      ai_model: string;
      status: string;
      error?: string;
    }>;
  };
}> => {
  const params = new URLSearchParams();
  if (sport) params.append('sport', sport);
  params.append('days_back', daysBack.toString());
  if (aiProvider) params.append('ai_provider', aiProvider);
  if (adminId) params.append('admin_id', adminId.toString());

  const response = await apiClient.post(`/api/notion/sync/unsynced?${params.toString()}`);
  return response.data;
};

// 노션 동기화 상태 조회
export const getNotionSyncStatus = async (
  sport: string,
  date: string,
  adminId?: number
): Promise<{
  status: string;
  data: {
    sport: string;
    date: string;
    matches: Array<{
      match_id: number;
      home_team: string;
      away_team: string;
      match_time: string;
      db_predictions: Array<{
        ai_model: string;
        version: number;
        created_at: string | null;
      }>;
      notion_page_exists: boolean;
      notion_predictions: string[];
    }>;
  };
}> => {
  const params = new URLSearchParams({
    sport: sport,
    date: date
  });
  if (adminId) params.append('admin_id', adminId.toString());

  const response = await apiClient.get(`/api/notion/sync/status?${params.toString()}`);
  return response.data;
};

// ===== Notion Pages Management API (Administrator Only) =====
// 간소화된 구조: admin 1명당 1개의 Notion 페이지만 연결 (admins.notion_page_id FK)

export interface NotionPage {
  page_id: number;
  page_name: string;
  notion_token: string;
  notion_token_full: string;
  database_id: string;
  description: string | null;
  is_active: boolean;
  assignment_count: number;  // 이 페이지에 연결된 관리자 수
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

// 간소화된 할당 정보 (admin 1명 = 1개 페이지)
export interface NotionAssignment {
  admin_id: number;
  admin_name: string | null;
  page_id: number;
  page_name: string | null;
  database_id: string | null;
  is_active: boolean;
}

// Notion 페이지 목록 조회
export const getNotionPages = async (includeInactive: boolean = false): Promise<{
  status: string;
  data: NotionPage[];
  total: number;
}> => {
  const params = new URLSearchParams();
  if (includeInactive) params.append('include_inactive', 'true');

  const response = await apiClient.get(`/api/notion-pages?${params.toString()}`);
  return response.data;
};

// Notion 페이지 생성
export const createNotionPage = async (data: {
  page_name: string;
  notion_token: string;
  database_id: string;
  description?: string;
}): Promise<{
  status: string;
  message: string;
  data: { page_id: number; page_name: string; database_id: string };
}> => {
  const response = await apiClient.post('/api/notion-pages', data);
  return response.data;
};

// Notion 페이지 수정
export const updateNotionPage = async (pageId: number, data: {
  page_name?: string;
  notion_token?: string;
  database_id?: string;
  description?: string;
  is_active?: boolean;
}): Promise<{
  status: string;
  message: string;
  data: { page_id: number; page_name: string; is_active: boolean };
}> => {
  const response = await apiClient.put(`/api/notion-pages/${pageId}`, data);
  return response.data;
};

// Notion 페이지 삭제
export const deleteNotionPage = async (pageId: number): Promise<{
  status: string;
  message: string;
}> => {
  const response = await apiClient.delete(`/api/notion-pages/${pageId}`);
  return response.data;
};

// 관리자별 Notion 페이지 할당 현황 조회 (간소화)
export const getNotionAssignments = async (): Promise<{
  status: string;
  data: NotionAssignment[];
  total: number;
}> => {
  const response = await apiClient.get('/api/notion-pages/assignments');
  return response.data;
};

// 관리자에게 Notion 페이지 할당 (1:1 관계, 기존 할당 덮어씀)
export const assignNotionPage = async (adminId: number, pageId: number): Promise<{
  status: string;
  message: string;
  data: { admin_id: number; page_id: number; page_name: string };
}> => {
  const response = await apiClient.put(`/api/notion-pages/assign/${adminId}`, {
    page_id: pageId
  });
  return response.data;
};

// 관리자의 Notion 페이지 할당 해제
export const unassignNotionPage = async (adminId: number): Promise<{
  status: string;
  message: string;
}> => {
  const response = await apiClient.delete(`/api/notion-pages/assign/${adminId}`);
  return response.data;
};

// 내 Notion 페이지 조회 (단일, 1:1 관계)
export const getMyNotionPage = async (): Promise<{
  status: string;
  data: {
    page_id: number;
    page_name: string;
    database_id: string;
    description: string | null;
  } | null;
  message?: string;
}> => {
  const response = await apiClient.get('/api/notion-pages/my-page');
  return response.data;
};
